import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";
import api from "../../services/api";
import Button from "../../components/common/Button/Button";
import Input from "../../components/common/Input/Input";
import SearchSelect from "../../components/common/SearchSelect/SearchSelect";
import styles from "./CreateInvoiceForm.module.css";

const CreateInvoiceForm = ({ onSuccess, onCancel, initialData }) => {
  const [submitting, setSubmitting] = useState(false);
  const [submitType, setSubmitType] = useState(null);
  const [channels, setChannels] = useState({ push: true, email: true });
  const [resultData, setResultData] = useState(null);

  const [formData, setFormData] = useState({
    user_id: initialData?.user_id || "",
    user_gst_number: initialData?.user_gst_number || "",
    plan_name: initialData?.plan_name || "",
    plan_validity: initialData?.plan_validity || "1 month",
    invoice_amount: initialData?.invoice_amount || 0,
    discount_amount: initialData?.discount_amount || 0,
    cgst_rate: initialData?.cgst_rate !== undefined ? initialData.cgst_rate : 0,
    sgst_rate: initialData?.sgst_rate !== undefined ? initialData.sgst_rate : 0,
    igst_rate: initialData?.igst_rate !== undefined ? initialData.igst_rate : 18,
  });

  const [users, setUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [userSearchError, setUserSearchError] = useState("");

  const [plans, setPlans] = useState([]);
  const [loadingPlans, setLoadingPlans] = useState(false);

  useEffect(() => {
    if (initialData?.user_id) {
      setUsers([{
        value: initialData.user_id,
        label: `${initialData.user_name || 'User'} - ID: ${initialData.user_id}`,
        gst_number: initialData.user_gst_number || ""
      }]);
    } else {
      setUsers([]);
    }
    fetchPlans();
  }, [initialData]);

  const fetchPlans = async () => {
    try {
      setLoadingPlans(true);
      const res = await api.get('/plans-new');
      const planList = res?.result || res?.data?.result || [];
      if (Array.isArray(planList)) {
        const activePlans = planList.filter(p => p.active_status === 'Yes' || p.active_status === true || String(p.status).toLowerCase() === 'active');
        setPlans(activePlans);
      }
    } catch (err) {
      console.error("Failed to fetch plans", err);
    } finally {
      setLoadingPlans(false);
    }
  };

  const updatePrices = (selectedPlan) => {
    const fullPrice = Number(selectedPlan.price_full) || 0;
    const discPrice = selectedPlan.price_discounted !== null && selectedPlan.price_discounted !== undefined 
      ? Number(selectedPlan.price_discounted) 
      : fullPrice;
    const discountVal = fullPrice - discPrice;

    setFormData(prev => ({
      ...prev,
      invoice_amount: fullPrice,
      discount_amount: Math.max(0, discountVal)
    }));
  };

  const handlePlanSelect = (val) => {
    const valNormalized = (val || "").trim().toLowerCase();
    const matchedPlans = plans.filter(p => (p.name || "").trim().toLowerCase() === valNormalized);
    let newValidity = "";
    let selectedPlan = null;

    if (matchedPlans.length === 1) {
      newValidity = matchedPlans[0].validity || "";
      selectedPlan = matchedPlans[0];
    } else if (matchedPlans.length > 1) {
      newValidity = "";
      selectedPlan = null;
    }

    setFormData(prev => ({
      ...prev,
      plan_name: val,
      plan_validity: newValidity
    }));

    if (selectedPlan) {
      updatePrices(selectedPlan);
    } else {
      setFormData(prev => ({
        ...prev,
        invoice_amount: 0,
        discount_amount: 0
      }));
    }
  };

  const handleValiditySelect = (val) => {
    setFormData(prev => ({ ...prev, plan_validity: val }));
    const currentPlanName = (formData.plan_name || "").trim().toLowerCase();
    const selectedPlan = plans.find(p => (p.name || "").trim().toLowerCase() === currentPlanName && p.validity === val);
    if (selectedPlan) {
      updatePrices(selectedPlan);
    }
  };

  const fetchUsers = async (searchTerm) => {
    setUserSearchError("");
    if (!searchTerm || !searchTerm.trim()) {
      setUsers([]);
      return;
    }
    
    try {
      setLoadingUsers(true);
      const url = `/users?search=${encodeURIComponent(searchTerm)}&limit=20`;
      const res = await api.get(url);
      const userList = res?.data?.data || res?.data || [];
      if (Array.isArray(userList)) {
        if (userList.length === 0 && /^\d{10}$/.test(searchTerm.trim())) {
          setUserSearchError("No user found");
        }
        setUsers(userList.map(u => ({
          value: u.userid,
          label: `${u.name || u.username || 'User'} - ID: ${u.userid}`,
          gst_number: u.gst_number || ""
        })));
      }
    } catch (err) {
      console.error("Failed to fetch users", err);
    } finally {
      setLoadingUsers(false);
    }
  };

  const handleUserSearch = (val) => {
    fetchUsers(val);
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const baseAmount = Number(formData.invoice_amount) || 0;
  const discount = Number(formData.discount_amount) || 0;
  const netAmount = Math.max(0, baseAmount - discount);

  const cgstAmount = netAmount * ((Number(formData.cgst_rate) || 0) / 100);
  const sgstAmount = netAmount * ((Number(formData.sgst_rate) || 0) / 100);
  const igstAmount = netAmount * ((Number(formData.igst_rate) || 0) / 100);
  const totalGst = cgstAmount + sgstAmount + igstAmount;
  
  const totalAmount = netAmount + totalGst;

  const handleSubmit = async (shouldSend) => {
    if (!formData.user_id) {
      toast.error("Please select a user");
      return;
    }

    const currentPlanName = (formData.plan_name || "").trim().toLowerCase();
    if (!currentPlanName) {
      toast.error("Please select a Plan Name");
      return;
    }

    const matchedPlans = plans.filter(p => (p.name || "").trim().toLowerCase() === currentPlanName);
    if (matchedPlans.length === 0) {
      toast.error("Please select a valid Plan Name from the list");
      return;
    }

    const isValidValidity = matchedPlans.some(p => p.validity === formData.plan_validity);
    if (!isValidValidity) {
      toast.error("Please select a valid Plan Validity from the list");
      return;
    }

    if (totalAmount <= 0) {
      toast.error("Total amount must be greater than 0");
      return;
    }

    setSubmitting(true);
    setSubmitType(shouldSend ? "send" : "create");
    try {
      const channelsArray = [];
      if (shouldSend) {
        if (channels.push) channelsArray.push("push");
        if (channels.email) channelsArray.push("email");
      }

      const payload = {
        user_id: formData.user_id,
        user_gst_number: formData.user_gst_number || "",
        plan_name: formData.plan_name || 'Custom Plan',
        plan_validity: formData.plan_validity,
        invoice_amount: baseAmount,
        discount_amount: discount,
        invoice_amount_net: netAmount,
        cgst_rate: Number(formData.cgst_rate) || 0,
        cgst: cgstAmount,
        sgst_rate: Number(formData.sgst_rate) || 0,
        sgst: sgstAmount,
        igst_rate: Number(formData.igst_rate) || 0,
        igst: igstAmount,
        total_invoice_amount: totalAmount,
        channels: JSON.stringify(channelsArray),
      };

      let res;
      if (initialData && initialData.internal_transaction_id) {
        res = await api.put(`/invoice/${initialData.internal_transaction_id}`, payload);
      } else {
        res = await api.post("/invoice", payload);
      }
      
      if (res.status) {
        toast.success(initialData ? "Draft Invoice updated successfully!" : "Draft Invoice generated successfully!");
        setResultData({
          transaction_id: res.internal_transaction_id,
          notifications: res.notifications
        });
      }
    } catch (err) {
      toast.error(err.message || "Failed to create invoice");
    } finally {
      setSubmitting(false);
      setSubmitType(null);
    }
  };

    if (resultData) {
        return (
            <div className={styles.resultContainer}>
                <div className={styles.resultCard}>
                    <div className={styles.resultTitle}>Response Result</div>
                    <div className={styles.resultList}>
                        <div className={styles.resultItem}>
                            <span className={styles.resultSuccess}>✓</span> Invoice {initialData ? "updated" : "saved"} successfully.
                        </div>
                        
                        {resultData.notifications?.push && (
                            <div className={styles.resultItem}>
                                {resultData.notifications.push.success ? (
                                    <span className={styles.resultSuccess}>✓</span>
                                ) : (
                                    <span className={styles.resultError}>✗</span>
                                )} 
                                Push notification {resultData.notifications.push.success ? 'sent successfully.' : `failed: ${resultData.notifications.push.message || 'Error'}`}
                            </div>
                        )}

                        {resultData.notifications?.email && (
                            <div className={styles.resultItem}>
                                {resultData.notifications.email.success ? (
                                    <span className={styles.resultSuccess}>✓</span>
                                ) : (
                                    <span className={styles.resultError}>✗</span>
                                )} 
                                Email notification {resultData.notifications.email.success ? 'sent successfully.' : `failed: ${resultData.notifications.email.message || 'Error'}`}
                            </div>
                        )}
                    </div>
                    <button 
                        type="button" 
                        className={`${styles.submitBtn || ''} ${styles.resultCloseBtn}`}
                        onClick={onSuccess}
                        style={{ background: "var(--color-primary, #0f172a)", color: "white", padding: "10px", border: "none", borderRadius: "5px", cursor: "pointer", fontWeight: "bold" }}
                    >
                        Close
                    </button>
                </div>
            </div>
        );
    }

  const uniquePlanNames = [...new Set(plans.map(p => (p.name || "").trim()))].filter(Boolean);
  const planNameOptions = uniquePlanNames.map(name => ({ value: name, label: name }));

  const currentPlanName = (formData.plan_name || "").trim().toLowerCase();
  const availableValidities = plans
    .filter(p => (p.name || "").trim().toLowerCase() === currentPlanName && p.validity)
    .map(p => p.validity);
  const validityOptions = [...new Set(availableValidities)].map(v => ({ value: v, label: v }));

  return (
    <form className={styles.formContainer} onSubmit={handleSubmit} noValidate>
      <div className={styles.grid2}>
        <div className={styles.fieldGroup}>
          <SearchSelect
            label="Select User"
            name="user_id"
            placeholder="Search User (ID, Name, or Phone)..."
            options={users}
            value={formData.user_id}
            onSearch={handleUserSearch}
            error={userSearchError}
            isLoading={loadingUsers}
            onChange={(e) => {
              const selectedUser = users.find(u => u.value === e.target.value);
              handleInputChange("user_id", e.target.value);
              setUserSearchError("");
              if (selectedUser) {
                handleInputChange("user_gst_number", selectedUser.gst_number || "");
              } else {
                handleInputChange("user_gst_number", "");
              }
            }}
            required
          />
        </div>
        
        <Input
          label="GST Number (Optional)"
          value={formData.user_gst_number}
          onChange={(e) => handleInputChange("user_gst_number", e.target.value)}
          placeholder="Enter GST manually or auto-filled"
        />

        <div className={styles.fieldGroup}>
          <label>Plan/Service Name</label>
          <SearchSelect
            options={planNameOptions}
            value={formData.plan_name}
            onChange={(e) => handlePlanSelect(e.target.value)}
            placeholder="Select a Plan"
            isLoading={loadingPlans}
          />
        </div>
        
        {availableValidities.length > 0 ? (
          <div className={styles.fieldGroup}>
            <label>Plan Validity</label>
            <SearchSelect
              options={validityOptions}
              value={formData.plan_validity}
              onChange={(e) => handleValiditySelect(e.target.value)}
              placeholder="Select Validity"
            />
          </div>
        ) : (
          <Input
            label="Plan Validity"
            value={formData.plan_validity}
            onChange={(e) => handleInputChange("plan_validity", e.target.value)}
            placeholder="e.g. 1 month, 1 year"
          />
        )}
      </div>

      <div className={styles.sectionDivider}>Amount Details</div>
      
      <div className={styles.grid2}>
        <Input
          type="number"
          label="Base Invoice Amount (₹)"
          value={formData.invoice_amount}
          onChange={(e) => handleInputChange("invoice_amount", e.target.value)}
          min="0"
        />
        <Input
          type="number"
          label="Discount Amount (₹)"
          value={formData.discount_amount}
          onChange={(e) => handleInputChange("discount_amount", e.target.value)}
          min="0"
        />
      </div>


      <div className={styles.summaryBox}>
        <div className={styles.summaryRow}>
          <span>Net Amount:</span>
          <span>₹{netAmount.toFixed(2)}</span>
        </div>
        <div className={styles.summaryRow}>
          <span>IGST 18%:</span>
          <span>₹{totalGst.toFixed(2)}</span>
        </div>
        <div className={`${styles.summaryRow} ${styles.summaryTotal}`}>
          <span>Total Payable:</span>
          <span>₹{totalAmount.toFixed(2)}</span>
        </div>
      </div>

      <div style={{ marginBottom: "20px", display: "flex", gap: "20px", alignItems: "center" }}>
        <strong style={{ fontSize: "14px" }}>Notify User:</strong>
        <label style={{ display: "flex", alignItems: "center", gap: "8px", fontSize: "14px", cursor: "pointer" }}>
          <input 
            type="checkbox" 
            checked={channels.push}
            onChange={(e) => setChannels(prev => ({ ...prev, push: e.target.checked }))}
            style={{ width: "16px", height: "16px", cursor: "pointer" }}
          />
          Push Notification
        </label>
        <label style={{ display: "flex", alignItems: "center", gap: "8px", fontSize: "14px", cursor: "pointer" }}>
          <input 
            type="checkbox" 
            checked={channels.email}
            onChange={(e) => setChannels(prev => ({ ...prev, email: e.target.checked }))}
            style={{ width: "16px", height: "16px", cursor: "pointer" }}
          />
          Email Notification
        </label>
      </div>

      <div className={styles.formActions}>
        <Button variant="secondary" onClick={(e) => { e.preventDefault(); onCancel(); }} disabled={submitting}>Cancel</Button>
        <Button 
          variant="primary" 
          onClick={(e) => { e.preventDefault(); handleSubmit(false); }} 
          isLoading={submitting && submitType === "create"} 
          disabled={submitting}
        >
          Create
        </Button>
        <Button 
          variant="primary" 
          onClick={(e) => { e.preventDefault(); handleSubmit(true); }} 
          isLoading={submitting && submitType === "send"} 
          disabled={submitting}
        >
          Submit
        </Button>
      </div>
    </form>
  );
};

export default CreateInvoiceForm;
